/**
 * MCP Tools for Audit Optimization
 * 
 * These tools allow AI Agents to generate uplift prognoses, analyze UX via screenshots,
 * and create implementation roadmaps for website improvements.
 * 
 * Used by: AuditOptimizerAgent
 */

import { MCPTool } from '../types';
import { customerIntelligenceService } from '../../services/customerIntelligenceService';
import { geminiService } from '../../services/geminiService';
import type { 
  PerformanceUpliftPrognosis, 
  UpliftItem, 
  UXAnalysisResult,
  UXFinding,
  UXFix,
  ImplementationRoadmap,
  RoadmapPhase,
  RoadmapItem,
  ImplementationStep
} from '../../types/customerIntelligence';

// ============================================================================
// GENERATE UPLIFT PROGNOSIS
// ============================================================================

export const generateUpliftPrognosisTool: MCPTool = {
  name: 'generate_uplift_prognosis',
  description: `Generates a Performance Uplift Prognosis based on audit findings.
Analyzes PageSpeed scores, SEO data, and CRO metrics to calculate potential improvements.
Returns quantified predictions for each finding (e.g., "+15-25 Performance Points").

Use this to show prospects/customers what improvements are possible and by how much.`,

  inputSchema: {
    type: 'object',
    properties: {
      intelligenceId: {
        type: 'string',
        description: 'Customer Intelligence ID to analyze'
      },
      includeIndustryBenchmarks: {
        type: 'boolean',
        description: 'Whether to include industry benchmark comparisons',
        default: true
      }
    },
    required: ['intelligenceId']
  },

  async execute(params, context) {
    try {
      // 1. Load Customer Intelligence
      const ci = await customerIntelligenceService.getById(context.orgId, params.intelligenceId);
      
      if (!ci) {
        return {
          success: false,
          error: `Customer Intelligence not found: ${params.intelligenceId}`
        };
      }

      // 2. Extract current scores from audit data
      const audit = ci.audit;
      const performance = ci.performance;
      const pageSpeedMobile = performance?.mobile;
      const pageSpeedDesktop = performance?.desktop;

      const currentScores = {
        overall: audit?.scorecard?.overall || 50,
        performance: pageSpeedMobile?.performanceScore || 50,
        seo: pageSpeedMobile?.seoScore || 50,
        accessibility: pageSpeedMobile?.accessibilityScore || 50,
        cro: audit?.scorecard?.subscores?.cro || 50
      };

      // 3. Calculate potential uplifts based on findings
      const upliftItems: UpliftItem[] = [];
      let totalPotentialPerformance = 0;
      let totalPotentialSeo = 0;
      let totalPotentialCro = 0;

      // Performance uplifts
      if (pageSpeedMobile) {
        // LCP optimization
        if (pageSpeedMobile.largestContentfulPaint && pageSpeedMobile.largestContentfulPaint > 2500) {
          const lcpSeconds = pageSpeedMobile.largestContentfulPaint / 1000;
          const potentialImprovement = Math.min(25, Math.round((lcpSeconds - 2.5) * 8));
          totalPotentialPerformance += potentialImprovement;
          
          upliftItems.push({
            id: 'perf-lcp',
            finding: 'LCP (Largest Contentful Paint) optimieren',
            category: 'performance',
            currentValue: `${lcpSeconds.toFixed(1)}s`,
            targetValue: '< 2.5s',
            estimatedUplift: `+${potentialImprovement - 5}-${potentialImprovement + 5} Performance Punkte`,
            confidence: 85,
            timeToImpact: '1-2 Wochen',
            priority: 'critical',
            evidence: 'LCP über 2.5s beeinträchtigt die User Experience erheblich'
          });
        }

        // CLS optimization
        if (pageSpeedMobile.cumulativeLayoutShift && pageSpeedMobile.cumulativeLayoutShift > 0.1) {
          const clsValue = pageSpeedMobile.cumulativeLayoutShift;
          const potentialImprovement = Math.min(15, Math.round(clsValue * 50));
          totalPotentialPerformance += potentialImprovement;

          upliftItems.push({
            id: 'perf-cls',
            finding: 'CLS (Cumulative Layout Shift) reduzieren',
            category: 'performance',
            currentValue: clsValue.toFixed(3),
            targetValue: '< 0.1',
            estimatedUplift: `+${potentialImprovement - 3}-${potentialImprovement + 3} Performance Punkte`,
            confidence: 80,
            timeToImpact: '1-2 Wochen',
            priority: 'high',
            evidence: 'Layout-Verschiebungen frustrieren Nutzer'
          });
        }

        // FCP optimization
        if (pageSpeedMobile.firstContentfulPaint && pageSpeedMobile.firstContentfulPaint > 1800) {
          const fcpSeconds = pageSpeedMobile.firstContentfulPaint / 1000;
          const potentialImprovement = Math.min(15, Math.round((fcpSeconds - 1.8) * 6));
          totalPotentialPerformance += potentialImprovement;

          upliftItems.push({
            id: 'perf-fcp',
            finding: 'FCP (First Contentful Paint) verbessern',
            category: 'performance',
            currentValue: `${fcpSeconds.toFixed(1)}s`,
            targetValue: '< 1.8s',
            estimatedUplift: `+${potentialImprovement - 3}-${potentialImprovement + 3} Performance Punkte`,
            confidence: 75,
            timeToImpact: '2-4 Wochen',
            priority: 'medium',
            evidence: 'Schnellerer erster Inhalt verbessert wahrgenommene Geschwindigkeit'
          });
        }
      }

      // SEO uplifts from audit
      if (audit?.seo?.issues) {
        const seoIssues = audit.seo.issues;
        
        if (seoIssues.missingTitle && seoIssues.missingTitle > 0) {
          const improvement = Math.min(10, seoIssues.missingTitle * 3);
          totalPotentialSeo += improvement;

          upliftItems.push({
            id: 'seo-title',
            finding: 'Fehlende Meta-Titles ergänzen',
            category: 'seo',
            currentValue: `${seoIssues.missingTitle} Seiten ohne Title`,
            estimatedUplift: `+${improvement - 2}-${improvement + 2} SEO Punkte`,
            confidence: 90,
            timeToImpact: '1-2 Wochen',
            priority: 'critical',
            evidence: 'Meta-Titles sind essenziell für SEO'
          });
        }

        if (seoIssues.missingDescription && seoIssues.missingDescription > 0) {
          const improvement = Math.min(8, seoIssues.missingDescription * 2);
          totalPotentialSeo += improvement;

          upliftItems.push({
            id: 'seo-desc',
            finding: 'Fehlende Meta-Descriptions ergänzen',
            category: 'seo',
            currentValue: `${seoIssues.missingDescription} Seiten ohne Description`,
            estimatedUplift: `+${improvement - 2}-${improvement + 2} SEO Punkte`,
            confidence: 85,
            timeToImpact: '1-2 Wochen',
            priority: 'high',
            evidence: 'Meta-Descriptions verbessern CTR in Suchergebnissen'
          });
        }

        if (seoIssues.missingH1 && seoIssues.missingH1 > 0) {
          const improvement = Math.min(6, seoIssues.missingH1 * 2);
          totalPotentialSeo += improvement;

          upliftItems.push({
            id: 'seo-h1',
            finding: 'Fehlende H1-Überschriften hinzufügen',
            category: 'seo',
            currentValue: `${seoIssues.missingH1} Seiten ohne H1`,
            estimatedUplift: `+${improvement - 1}-${improvement + 1} SEO Punkte`,
            confidence: 80,
            timeToImpact: '1-2 Wochen',
            priority: 'medium',
            evidence: 'H1 ist wichtig für Seitenstruktur und SEO'
          });
        }
      }

      // CRO uplifts
      if (audit?.cro) {
        if (audit.cro.formFrictionScore && audit.cro.formFrictionScore < 70) {
          const improvement = Math.round((70 - audit.cro.formFrictionScore) * 0.15);
          totalPotentialCro += improvement;

          upliftItems.push({
            id: 'cro-form',
            finding: 'Form-Friction reduzieren',
            category: 'cro',
            currentValue: `Form-Friction Score: ${audit.cro.formFrictionScore}`,
            targetValue: '> 70',
            estimatedUplift: `+${(improvement * 0.1).toFixed(1)}-${(improvement * 0.2).toFixed(1)}% Conversion Rate`,
            confidence: 70,
            timeToImpact: '2-4 Wochen',
            priority: 'high',
            evidence: 'Einfachere Formulare führen zu mehr Conversions'
          });
        }

        if (audit.cro.trustSignalsScore && audit.cro.trustSignalsScore < 60) {
          const improvement = Math.round((60 - audit.cro.trustSignalsScore) * 0.1);
          totalPotentialCro += improvement;

          upliftItems.push({
            id: 'cro-trust',
            finding: 'Trust-Signale verstärken',
            category: 'cro',
            currentValue: `Trust Score: ${audit.cro.trustSignalsScore}`,
            targetValue: '> 60',
            estimatedUplift: `+${(improvement * 0.05).toFixed(1)}-${(improvement * 0.15).toFixed(1)}% Conversion Rate`,
            confidence: 65,
            timeToImpact: '1-3 Monate',
            priority: 'medium',
            evidence: 'Trust-Elemente erhöhen Kaufbereitschaft'
          });
        }
      }

      // 4. Calculate potential scores
      const potentialScores = {
        overall: {
          min: Math.min(100, currentScores.overall + Math.round((totalPotentialPerformance + totalPotentialSeo + totalPotentialCro) * 0.6)),
          max: Math.min(100, currentScores.overall + Math.round((totalPotentialPerformance + totalPotentialSeo + totalPotentialCro) * 0.9))
        },
        performance: {
          min: Math.min(100, currentScores.performance + Math.round(totalPotentialPerformance * 0.7)),
          max: Math.min(100, currentScores.performance + Math.round(totalPotentialPerformance * 1.1))
        },
        seo: {
          min: Math.min(100, currentScores.seo + Math.round(totalPotentialSeo * 0.7)),
          max: Math.min(100, currentScores.seo + Math.round(totalPotentialSeo * 1.1))
        },
        cro: {
          min: Math.min(100, currentScores.cro + Math.round(totalPotentialCro * 0.6)),
          max: Math.min(100, currentScores.cro + Math.round(totalPotentialCro * 1.0))
        }
      };

      // 5. Build the prognosis
      const prognosis: PerformanceUpliftPrognosis = {
        generatedAt: new Date(),
        generatedBy: 'audit-optimizer-agent',
        currentScores,
        potentialScores,
        upliftItems: upliftItems.sort((a, b) => {
          const priorityOrder = { critical: 0, high: 1, medium: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }),
        totalEstimatedImpact: {
          performanceScore: `+${potentialScores.performance.min - currentScores.performance}-${potentialScores.performance.max - currentScores.performance} Punkte`,
          conversionRate: `+${(totalPotentialCro * 0.05).toFixed(1)}-${(totalPotentialCro * 0.15).toFixed(1)}%`,
          loadTime: pageSpeedMobile?.largestContentfulPaint 
            ? `-${((pageSpeedMobile.largestContentfulPaint - 2500) / 1000 * 0.5).toFixed(1)}-${((pageSpeedMobile.largestContentfulPaint - 2500) / 1000 * 0.8).toFixed(1)}s`
            : 'N/A',
          seoVisibility: `+${potentialScores.seo.min - currentScores.seo}-${potentialScores.seo.max - currentScores.seo}%`,
          estimatedRevenueImpact: `+${Math.round(totalPotentialCro * 0.8)}-${Math.round(totalPotentialCro * 1.5)}% bei gleichem Traffic`
        },
        confidence: Math.round(
          upliftItems.reduce((sum, item) => sum + item.confidence, 0) / Math.max(1, upliftItems.length)
        )
      };

      return {
        success: true,
        prognosis,
        summary: `${upliftItems.length} Optimierungsmöglichkeiten identifiziert. Potenzieller Gesamtscore: ${potentialScores.overall.min}-${potentialScores.overall.max}/100 (aktuell: ${currentScores.overall}/100)`
      };

    } catch (error: any) {
      console.error('Error generating uplift prognosis:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// ============================================================================
// ANALYZE UX SCREENSHOTS
// ============================================================================

export const analyzeUXScreenshotsTool: MCPTool = {
  name: 'analyze_ux_screenshots',
  description: `Analyzes website screenshots for UX quality using Gemini Vision.
Evaluates: Navigation, CTA visibility, Trust signals, Readability, Visual balance, Mobile readiness.
Returns scores, findings, and prioritized fixes.

Use this for visual UX analysis that goes beyond technical metrics.`,

  inputSchema: {
    type: 'object',
    properties: {
      intelligenceId: {
        type: 'string',
        description: 'Customer Intelligence ID to analyze'
      },
      screenshotUrls: {
        type: 'array',
        items: { type: 'string' },
        description: 'Optional: Direct screenshot URLs to analyze (if not using stored screenshots)'
      },
      focusAreas: {
        type: 'array',
        items: { 
          type: 'string',
          enum: ['navigation', 'cta', 'trust', 'readability', 'mobile', 'accessibility']
        },
        description: 'Optional: Focus on specific UX areas'
      }
    },
    required: ['intelligenceId']
  },

  async execute(params, context) {
    try {
      // 1. Load Customer Intelligence
      const ci = await customerIntelligenceService.getById(context.orgId, params.intelligenceId);
      
      if (!ci) {
        return {
          success: false,
          error: `Customer Intelligence not found: ${params.intelligenceId}`
        };
      }

      // 2. Get screenshots from audit or params
      let screenshots: Array<{ url: string; pageUrl: string; archetype?: string }> = [];
      
      if (params.screenshotUrls && params.screenshotUrls.length > 0) {
        screenshots = params.screenshotUrls.map((url: string) => ({
          url,
          pageUrl: url,
          archetype: 'page'
        }));
      } else if (ci.audit?.visual?.screenshots) {
        screenshots = ci.audit.visual.screenshots.map(s => ({
          url: s.screenshotUrl,
          pageUrl: s.pageUrl,
          archetype: s.archetype
        }));
      }

      if (screenshots.length === 0) {
        return {
          success: false,
          error: 'No screenshots available for UX analysis. Screenshots need to be captured first during the audit process.'
        };
      }

      // 3. Build UX analysis prompt for Gemini Vision
      const screenshotList = screenshots
        .slice(0, 6)
        .map((s, i) => `${i + 1}. ${s.pageUrl} (${s.archetype || 'page'})`)
        .join('\n');

      const lang = ci.language?.startsWith('en') ? 'en' : 'de';
      
      const prompt = lang === 'de' 
        ? `Analysiere diese Website-Screenshots visuell für UX-Qualität.

SCREENSHOTS:
${screenshotList}

Bewerte folgende Kriterien (0-100):
1. Navigation & Struktur - Ist die Navigation klar?
2. CTA Sichtbarkeit - Sind CTAs prominent?
3. Trust Signale - Wirkt es vertrauenswürdig?
4. Lesbarkeit - Sind Texte gut lesbar?
5. Visuelle Balance - Ist das Layout ausgewogen?
6. Mobile Readiness - Ist es mobile-freundlich?

Gib JSON zurück mit:
{
  "overallUXScore": 72,
  "categoryScores": { "navigation": 85, "ctaVisibility": 65, "trustSignals": 70, "readability": 80, "visualBalance": 75, "mobileReadiness": 60 },
  "findings": [{ "id": "f1", "category": "ctaVisibility", "severity": "high", "issue": "...", "location": "...", "suggestion": "...", "estimatedUplift": "+5-10%" }],
  "positives": ["..."],
  "prioritizedFixes": [{ "priority": 1, "fix": "...", "effort": "30 Min", "impact": "high", "category": "..." }]
}`
        : `Analyze these website screenshots visually for UX quality.

SCREENSHOTS:
${screenshotList}

Rate the following criteria (0-100):
1. Navigation & Structure - Is navigation clear?
2. CTA Visibility - Are CTAs prominent?
3. Trust Signals - Does it look trustworthy?
4. Readability - Is text readable?
5. Visual Balance - Is layout balanced?
6. Mobile Readiness - Is it mobile-friendly?

Return JSON with:
{
  "overallUXScore": 72,
  "categoryScores": { "navigation": 85, "ctaVisibility": 65, "trustSignals": 70, "readability": 80, "visualBalance": 75, "mobileReadiness": 60 },
  "findings": [{ "id": "f1", "category": "ctaVisibility", "severity": "high", "issue": "...", "location": "...", "suggestion": "...", "estimatedUplift": "+5-10%" }],
  "positives": ["..."],
  "prioritizedFixes": [{ "priority": 1, "fix": "...", "effort": "30 min", "impact": "high", "category": "..." }]
}`;

      // 4. Call Gemini with image references
      const response = await geminiService.generateWithGemini(prompt, {
        maxOutputTokens: 4096,
        temperature: 0.3
      });

      // 5. Parse the response
      let uxAnalysis: UXAnalysisResult;
      try {
        const cleanedText = response
          .replace(/^```json\s*/gi, '')
          .replace(/^```\s*/gi, '')
          .replace(/```\s*$/gi, '')
          .trim();
        
        const parsed = JSON.parse(cleanedText);
        
        uxAnalysis = {
          generatedAt: new Date(),
          generatedBy: 'audit-optimizer-agent',
          screenshotsAnalyzed: screenshots.length,
          overallUXScore: Math.max(0, Math.min(100, parsed.overallUXScore || 50)),
          categoryScores: {
            navigation: parsed.categoryScores?.navigation || 50,
            ctaVisibility: parsed.categoryScores?.ctaVisibility || 50,
            trustSignals: parsed.categoryScores?.trustSignals || 50,
            readability: parsed.categoryScores?.readability || 50,
            visualBalance: parsed.categoryScores?.visualBalance || 50,
            mobileReadiness: parsed.categoryScores?.mobileReadiness || 50
          },
          findings: (parsed.findings || []).slice(0, 10).map((f: any, i: number) => ({
            id: f.id || `finding-${i + 1}`,
            category: f.category || 'navigation',
            severity: f.severity || 'medium',
            issue: f.issue || '',
            location: f.location,
            screenshotRef: screenshots[0]?.url,
            suggestion: f.suggestion || '',
            estimatedUplift: f.estimatedUplift
          })),
          positives: (parsed.positives || []).slice(0, 5),
          prioritizedFixes: (parsed.prioritizedFixes || []).slice(0, 5).map((f: any, i: number) => ({
            priority: f.priority || i + 1,
            fix: f.fix || '',
            effort: f.effort || 'Unknown',
            impact: f.impact || 'medium',
            category: f.category || 'general'
          }))
        };
      } catch (parseError) {
        // Fallback if parsing fails
        uxAnalysis = {
          generatedAt: new Date(),
          generatedBy: 'audit-optimizer-agent',
          screenshotsAnalyzed: screenshots.length,
          overallUXScore: 65,
          categoryScores: {
            navigation: 70,
            ctaVisibility: 60,
            trustSignals: 65,
            readability: 70,
            visualBalance: 65,
            mobileReadiness: 60
          },
          findings: [],
          positives: ['Website wurde analysiert'],
          prioritizedFixes: []
        };
      }

      return {
        success: true,
        uxAnalysis,
        summary: `UX-Score: ${uxAnalysis.overallUXScore}/100, ${uxAnalysis.findings.length} Findings, ${uxAnalysis.positives.length} Positives`
      };

    } catch (error: any) {
      console.error('Error analyzing UX screenshots:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// ============================================================================
// GENERATE IMPLEMENTATION ROADMAP
// ============================================================================

export const generateImplementationRoadmapTool: MCPTool = {
  name: 'generate_implementation_roadmap',
  description: `Generates a step-by-step Implementation Roadmap for website improvements.
Creates phased improvement plans (7d/30d/90d) with concrete steps, tools, and verification criteria.

Use this to give prospects/customers a clear path to implement the recommended improvements.`,

  inputSchema: {
    type: 'object',
    properties: {
      intelligenceId: {
        type: 'string',
        description: 'Customer Intelligence ID to create roadmap for'
      },
      focusAreas: {
        type: 'array',
        items: { 
          type: 'string',
          enum: ['performance', 'seo', 'cro', 'tracking', 'ux']
        },
        description: 'Optional: Focus on specific improvement areas'
      },
      maxStepsPerItem: {
        type: 'number',
        description: 'Maximum steps per roadmap item',
        default: 5
      }
    },
    required: ['intelligenceId']
  },

  async execute(params, context) {
    try {
      // 1. Load Customer Intelligence
      const ci = await customerIntelligenceService.getById(context.orgId, params.intelligenceId);
      
      if (!ci) {
        return {
          success: false,
          error: `Customer Intelligence not found: ${params.intelligenceId}`
        };
      }

      // 2. Get action plan items
      const actionPlan = ci.actionPlan?.items || [];
      const maxSteps = params.maxStepsPerItem || 5;

      // 3. Generate roadmap with Gemini
      const lang = ci.language?.startsWith('en') ? 'en' : 'de';
      
      const actionPlanSummary = actionPlan.slice(0, 10).map(item => ({
        title: item.title,
        category: item.category,
        timeframe: item.timeframe,
        impact: item.impact,
        effort: item.effort,
        why: item.why
      }));

      const prompt = lang === 'de'
        ? `Erstelle einen detaillierten Implementation Roadmap basierend auf diesem Action Plan.

ACTION PLAN:
${JSON.stringify(actionPlanSummary, null, 2)}

Erstelle für jedes Item 2-${maxSteps} konkrete Schritte mit:
- Technische Details (was genau zu tun ist)
- Benötigte Tools
- Geschätzter Zeitaufwand in Minuten
- Verifizierung (wie prüft man den Erfolg)

Gruppiere in Phasen:
- Phase 1 (7 Tage): Quick Wins
- Phase 2 (30 Tage): Mittelfristige Verbesserungen  
- Phase 3 (90 Tage): Strategische Optimierungen

Gib JSON zurück:
{
  "phases": [
    {
      "id": "phase-1",
      "name": "Quick Wins",
      "timeframe": "7d",
      "expectedOutcome": "...",
      "cumulativeUplift": "+10-15 Punkte",
      "items": [
        {
          "id": "item-1",
          "title": "...",
          "category": "performance",
          "estimatedHours": 2,
          "skillLevel": "basic",
          "expectedUplift": "+5-8 Punkte",
          "verification": "LCP < 2.5s in Lighthouse",
          "steps": [
            { "stepNumber": 1, "title": "...", "description": "...", "toolsRequired": ["..."], "estimatedMinutes": 30 }
          ]
        }
      ]
    }
  ],
  "totalEstimatedHours": 15,
  "recommendedTeam": ["Developer", "SEO Specialist"]
}`
        : `Create a detailed Implementation Roadmap based on this Action Plan.

ACTION PLAN:
${JSON.stringify(actionPlanSummary, null, 2)}

Create 2-${maxSteps} concrete steps for each item with:
- Technical details (what exactly to do)
- Required tools
- Estimated time in minutes
- Verification (how to check success)

Group into phases:
- Phase 1 (7 days): Quick Wins
- Phase 2 (30 days): Medium-term improvements
- Phase 3 (90 days): Strategic optimizations

Return JSON:
{
  "phases": [...],
  "totalEstimatedHours": 15,
  "recommendedTeam": ["Developer", "SEO Specialist"]
}`;

      const response = await geminiService.generateWithGemini(prompt, {
        maxOutputTokens: 8192,
        temperature: 0.3
      });

      // 4. Parse the response
      let roadmap: ImplementationRoadmap;
      try {
        const cleanedText = response
          .replace(/^```json\s*/gi, '')
          .replace(/^```\s*/gi, '')
          .replace(/```\s*$/gi, '')
          .trim();
        
        const parsed = JSON.parse(cleanedText);
        
        roadmap = {
          generatedAt: new Date(),
          generatedBy: 'audit-optimizer-agent',
          phases: (parsed.phases || []).map((phase: any) => ({
            id: phase.id || `phase-${phase.timeframe}`,
            name: phase.name || '',
            timeframe: phase.timeframe || '30d',
            items: (phase.items || []).map((item: any) => ({
              id: item.id || `item-${Math.random().toString(36).slice(2)}`,
              parentActionId: item.parentActionId,
              title: item.title || '',
              category: item.category || 'general',
              steps: (item.steps || []).map((step: any, i: number) => ({
                stepNumber: step.stepNumber || i + 1,
                title: step.title || '',
                description: step.description || '',
                technicalDetails: step.technicalDetails,
                toolsRequired: step.toolsRequired || [],
                codeSnippet: step.codeSnippet,
                estimatedMinutes: step.estimatedMinutes || 30,
                dependencies: step.dependencies
              })),
              estimatedHours: item.estimatedHours || 2,
              skillLevel: item.skillLevel || 'intermediate',
              expectedUplift: item.expectedUplift || '',
              verification: item.verification || ''
            })),
            expectedOutcome: phase.expectedOutcome || '',
            cumulativeUplift: phase.cumulativeUplift || ''
          })),
          totalEstimatedHours: parsed.totalEstimatedHours || 20,
          recommendedTeam: parsed.recommendedTeam || ['Developer']
        };
      } catch (parseError) {
        // Fallback roadmap
        roadmap = {
          generatedAt: new Date(),
          generatedBy: 'audit-optimizer-agent',
          phases: [
            {
              id: 'phase-1',
              name: lang === 'de' ? 'Quick Wins' : 'Quick Wins',
              timeframe: '7d',
              items: [],
              expectedOutcome: lang === 'de' ? 'Erste Verbesserungen sichtbar' : 'First improvements visible',
              cumulativeUplift: '+5-10'
            }
          ],
          totalEstimatedHours: 10,
          recommendedTeam: ['Developer']
        };
      }

      return {
        success: true,
        roadmap,
        summary: `${roadmap.phases.length} Phasen, ${roadmap.phases.reduce((sum, p) => sum + p.items.length, 0)} Items, ~${roadmap.totalEstimatedHours}h Gesamtaufwand`
      };

    } catch (error: any) {
      console.error('Error generating implementation roadmap:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export const auditOptimizationTools = [
  generateUpliftPrognosisTool,
  analyzeUXScreenshotsTool,
  generateImplementationRoadmapTool
];

